/**
 * Admin Utilities - Helper functions for admin functionality
 */

// Admin user identification
export const isAdminUser = (user) => {
  if (!user) return false;

  // HARDCODED ADMIN EMAIL - CHANGE THIS TO YOUR SPECIFIC EMAIL
  const ADMIN_EMAIL = "rekhithasree@gmail.com";  // <<<< CHANGE THIS TO YOUR EMAIL

  // Check if user is superuser
  if (user.is_superuser) return true;

  // HARDCODED admin email check
  if (user.email && user.email.toLowerCase() === ADMIN_EMAIL.toLowerCase()) {
    return true;
  }

  // Additional admin emails (add more if needed)
  const additionalAdminEmails = [
    'admin@vasstr.com',
    'admin@fitcraft.com'
  ];

  return (
    user.email &&
    additionalAdminEmails.includes(user.email.toLowerCase())
  );
};

// Helper function to get the access token from authToken object
export const getAccessToken = () => {
  try {
    const authTokenObj = localStorage.getItem('authToken');
    if (!authTokenObj) {
      return null;
    }
    const parsedToken = JSON.parse(authTokenObj);
    return parsedToken.access || null;
  } catch (error) {
    console.error('Error parsing authToken from localStorage:', error);
    return null;
  }
};

// API call helper with admin authentication
export const adminApiCall = async (endpoint, options = {}) => {
  const token = getAccessToken();

  if (!token) {
    throw new Error('Admin authentication required');
  }

  const defaultOptions = {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  const mergedOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  };

  const response = await fetch(endpoint, mergedOptions);

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error('Admin access denied');
    } else if (response.status === 401) {
      throw new Error('Authentication required');
    } else {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }
  }

  return response.json();
};

// Format data for display
export const formatCellValue = (value, type) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  switch (type) {
    case 'boolean':
      return value ? '✓' : '✗';
    case 'datetime':
      return new Date(value).toLocaleString();
    case 'date':
      return new Date(value).toLocaleDateString();
    case 'email':
      return value;
    case 'number':
      return typeof value === 'number' ? value.toLocaleString() : value;
    case 'currency':
      return `₹${Number(value).toLocaleString()}`;
    case 'percentage':
      return `${Number(value)}%`;
    default:
      return String(value);
  }
};

// Export data to CSV
export const exportToCSV = (data, filename = 'export.csv') => {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }

  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row =>
      headers.map(header => {
        const value = row[header];
        // Escape commas and quotes in CSV
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',')
    )
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

// Debounce function for search
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Pagination utilities
export const getPaginationRange = (currentPage, totalPages, delta = 2) => {
  const range = [];
  const rangeWithDots = [];

  for (
    let i = Math.max(2, currentPage - delta);
    i <= Math.min(totalPages - 1, currentPage + delta);
    i++
  ) {
    range.push(i);
  }

  if (currentPage - delta > 2) {
    rangeWithDots.push(1, '...');
  } else {
    rangeWithDots.push(1);
  }

  rangeWithDots.push(...range);

  if (currentPage + delta < totalPages - 1) {
    rangeWithDots.push('...', totalPages);
  } else {
    rangeWithDots.push(totalPages);
  }

  return rangeWithDots;
};

// Validation utilities
export const validateFormData = (data, rules) => {
  const errors = {};

  Object.keys(rules).forEach(field => {
    const value = data[field];
    const rule = rules[field];

    if (rule.required && (!value || value.toString().trim() === '')) {
      errors[field] = `${field} is required`;
      return;
    }

    if (value && rule.type) {
      switch (rule.type) {
        case 'email':
          if (!/\S+@\S+\.\S+/.test(value)) {
            errors[field] = 'Invalid email format';
          }
          break;
        case 'number':
          if (isNaN(value)) {
            errors[field] = 'Must be a number';
          }
          break;
        case 'url':
          try {
            new URL(value);
          } catch {
            errors[field] = 'Invalid URL format';
          }
          break;
      }
    }

    if (value && rule.minLength && value.length < rule.minLength) {
      errors[field] = `Minimum ${rule.minLength} characters required`;
    }

    if (value && rule.maxLength && value.length > rule.maxLength) {
      errors[field] = `Maximum ${rule.maxLength} characters allowed`;
    }
  });

  return errors;
};

// Color utilities for status indicators
export const getStatusColor = (status) => {
  const statusColors = {
    active: 'success',
    inactive: 'error',
    pending: 'warning',
    approved: 'success',
    rejected: 'error',
    completed: 'success',
    processing: 'info',
    cancelled: 'error',
    true: 'success',
    false: 'error',
    yes: 'success',
    no: 'error',
  };

  return statusColors[String(status).toLowerCase()] || 'default';
};

// Local storage utilities
export const saveToLocalStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error('Failed to save to localStorage:', error);
  }
};

export const loadFromLocalStorage = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error('Failed to load from localStorage:', error);
    return defaultValue;
  }
};

// Toast notification helper
export const showToast = (message, type = 'info') => {
  // You can integrate this with your toast library
  console.log(`[${type.toUpperCase()}] ${message}`);
};

// Permission checking
export const hasPermission = (user, permission) => {
  // Implement based on your permission system
  if (isAdminUser(user)) return true;

  // Add more specific permission checks here
  return false;
};

// Table utilities
export const sortTableData = (data, sortBy, sortOrder) => {
  return [...data].sort((a, b) => {
    const aVal = a[sortBy];
    const bVal = b[sortBy];

    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      const result = aVal.localeCompare(bVal);
      return sortOrder === 'asc' ? result : -result;
    }

    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });
};

export const filterTableData = (data, searchTerm, searchFields) => {
  if (!searchTerm) return data;

  const lowerSearchTerm = searchTerm.toLowerCase();

  return data.filter(row =>
    searchFields.some(field => {
      const value = row[field];
      return value && value.toString().toLowerCase().includes(lowerSearchTerm);
    })
  );
};

export default {
  isAdminUser,
  adminApiCall,
  formatCellValue,
  exportToCSV,
  debounce,
  getPaginationRange,
  validateFormData,
  getStatusColor,
  saveToLocalStorage,
  loadFromLocalStorage,
  showToast,
  hasPermission,
  sortTableData,
  filterTableData,
};